/**
 * Session token storage — survives Capacitor WebView restarts via native
 * Preferences (Android SharedPreferences / iOS UserDefaults).
 *
 * IMPORTANT: do NOT return the Preferences plugin from an `async function`.
 * The plugin is a Proxy with a `.then` accessor; returning it from an async
 * function makes JS treat it as a thenable, which then calls the native
 * `then()` method that doesn't exist, throwing
 * `"Preferences.then()" is not implemented on android`. Keep the helper
 * synchronous (the underlying check `Capacitor.isPluginAvailable` is sync
 * anyway, so there's no real async work to do here).
 */
import { Capacitor } from "@capacitor/core";
// Static import (see admob.js comment for rationale on avoiding lazy imports)
import { Preferences } from "@capacitor/preferences";

const KEY = "session_token";
const DIAG_KEY = "fj_session_diag";

let _cached = null;
let _lastDiag = "";

function setDiag(msg) {
  _lastDiag = msg;
  try { localStorage.setItem(DIAG_KEY, msg); } catch (_) {}
}

// On-screen session diagnostic (release builds hide console logs, so we
// surface the last hydrate/save outcome in the UI instead).
export function getSessionDiag() {
  if (_lastDiag) return _lastDiag;
  try { return localStorage.getItem(DIAG_KEY) || ""; } catch (_) { return ""; }
}

// Append the /auth/me outcome (set by AuthContext) to the diagnostic line.
export function setAuthDiag(msg) {
  const base = getSessionDiag().split(" | ")[0];
  setDiag(base + " | " + msg);
}

function isNative() {
  try { return Capacitor.isNativePlatform(); } catch (_) { return false; }
}

function getPrefs() {
  if (!isNative()) return null;
  try {
    if (!Capacitor.isPluginAvailable("Preferences")) {
      setDiag("prefs=UNAVAILABLE");
      return null;
    }
  } catch (_) {
    return null;
  }
  return Preferences;
}

export async function hydrateSessionToken() {
  try {
    // 1. Read from native Preferences first (most reliable on Android)
    const prefs = getPrefs();
    if (prefs) {
      try {
        const { value } = await prefs.get({ key: KEY });
        if (value) {
          _cached = value;
          setDiag("prefs=FOUND(" + value.length + ")");
          try { localStorage.setItem(KEY, value); } catch (_) {}
          return value;
        }
        setDiag("prefs=EMPTY");
      } catch (e) {
        setDiag("prefs=THREW:" + String(e).slice(0, 40));
      }
    }
    // 2. Fall back to localStorage (web + older installs)
    try {
      const v = localStorage.getItem(KEY);
      if (v) {
        _cached = v;
        setDiag((getSessionDiag() || "") + " ls=FOUND(" + v.length + ")");
        if (prefs) {
          try { await prefs.set({ key: KEY, value: v }); } catch (_) {}
        }
        return v;
      }
      setDiag((getSessionDiag() || "") + " ls=EMPTY");
    } catch (_) {}
  } catch (_) {
    // Belt-and-braces: if anything else throws, swallow it so the app boot
    // doesn't crash. Worst case: user has to log in once more.
  }
  return null;
}

export function getSessionToken() {
  // Synchronous read — used by axios interceptor and AuthContext on mount.
  // Falls back to localStorage if hydrate hasn't been called yet (e.g.,
  // very fast first render).
  if (_cached != null) return _cached;
  try {
    const v = localStorage.getItem(KEY);
    if (v) _cached = v;
    return v;
  } catch (_) {
    return null;
  }
}

export async function setSessionToken(token) {
  _cached = token;
  try { localStorage.setItem(KEY, token); } catch (_) {}
  const prefs = getPrefs();
  if (prefs) {
    try {
      await prefs.set({ key: KEY, value: token });
      setDiag("saved: prefs=OK(" + (token ? token.length : 0) + ")");
    } catch (e) {
      setDiag("saved: prefs=THREW:" + String(e).slice(0, 40));
    }
  } else {
    setDiag("saved: prefs=UNAVAILABLE, ls only");
  }
}

export async function clearSessionToken() {
  _cached = null;
  try { localStorage.removeItem(KEY); } catch (_) {}
  try { localStorage.removeItem(SNAP_KEY); } catch (_) {}
  const prefs = getPrefs();
  if (prefs) {
    try { await prefs.remove({ key: KEY }); } catch (_) {}
    try { await prefs.remove({ key: SNAP_KEY }); } catch (_) {}
  }
}

// ---------------------------------------------------------------------------
// Auth snapshot — last-known {user, profile} so the app can open OFFLINE
// (native / airplane mode) when /auth/me can't be reached. Mirrored to both
// localStorage (fast, sync) and native Preferences (durable across WebView
// restarts). Only ever restored when a valid session token is present.
// ---------------------------------------------------------------------------
const SNAP_KEY = "fj_auth_snapshot";
let _snap = null;

export function cacheAuthSnapshot(user, profile) {
  if (!user) return;
  const payload = JSON.stringify({ user, profile, at: Date.now() });
  _snap = payload;
  try { localStorage.setItem(SNAP_KEY, payload); } catch (_) {}
  const prefs = getPrefs();
  if (prefs) { try { prefs.set({ key: SNAP_KEY, value: payload }); } catch (_) {} }
}

export async function hydrateAuthSnapshot() {
  // Pull the durable Preferences copy into localStorage so the sync getter
  // below can find it even if WebView localStorage was cleared.
  const prefs = getPrefs();
  if (prefs) {
    try {
      const { value } = await prefs.get({ key: SNAP_KEY });
      if (value) {
        _snap = value;
        try { localStorage.setItem(SNAP_KEY, value); } catch (_) {}
      }
    } catch (_) {}
  }
}

export function getCachedAuthSnapshot() {
  const raw = _snap || (() => { try { return localStorage.getItem(SNAP_KEY); } catch (_) { return null; } })();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && parsed.user ? parsed : null;
  } catch (_) { return null; }
}
